import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../api/client';

type TriageAction = 'archive' | 'manual_send_archive' | 'delete';

interface QueueRecord {
  request_id: string;
  received_at?: string;
  extracted_reference_number?: string;
  extracted_insured_name?: string;
  triage_class?: string;
  status?: string;
  delivery_status?: string;
  queue_type?: string;
  email_from?: string;
  extraction_data?: {
    period_requested?: string;
    notes?: string;
    confidence?: string;
    reference_number?: string;
    reference_numbers?: string[];
    insured_name?: string;
    policy_term_numbers?: string[];
  };
  fh_claim_data?: {
    claim_count?: number;
    claims?: any[];
    fh_policy_found?: boolean;
    total_incurred?: number;
    terms_checked?: string[];
  } | null;
  policy_data?: {
    broker_name?: string;
    insured_name?: string;
    reference_number?: string;
    [key: string]: any;
  } | null;
  pdf_s3_key?: string;
  template_s3_key?: string;
  send_attempts?: any[];
  approved_at?: string;
  approved_by?: string;
  dup_of?: string;
  email_subject?: string;
  email_message_id?: string;
  original_recipients_backup?: any;
  extraction_confidence?: string;
  triage_reason?: string;
  archived?: boolean;
  archived_at?: string;
  archived_by?: string;
  archived_reason?: string;
  archived_note?: string;
  deleted_manually?: boolean;
  deleted_at?: string;
  deleted_by?: string;
  parsed_at?: string;
  insurer?: string;
  insurer_name?: string;
}

interface TriageResult {
  succeeded: string[];
  skipped: { request_id: string; reason: string }[];
  failed: { request_id: string; reason: string }[];
}

const ACTION_META: Record<TriageAction, { label: string; confirmWord: string; btnClass: string; description: string }> = {
  archive: {
    label: 'Mark Archived',
    confirmWord: 'ARCHIVE',
    btnClass: 'bg-slate-600 hover:bg-slate-700 text-white',
    description: 'Archive these records. They were not delivered and are no longer relevant.',
  },
  manual_send_archive: {
    label: 'Manually Sent + Archived',
    confirmWord: 'SEND-ARCHIVE',
    btnClass: 'bg-blue-600 hover:bg-blue-700 text-white',
    description: 'Mark as sent outside Sidecar and archive. The loss run was delivered manually.',
  },
  delete: {
    label: 'Delete',
    confirmWord: 'DELETE',
    btnClass: 'bg-red-600 hover:bg-red-700 text-white',
    description: 'Soft-delete these records. They are noise or test artifacts.',
  },
};

function ageDays(receivedAt?: string): number {
  if (!receivedAt) return 0;
  const diff = Date.now() - new Date(receivedAt).getTime();
  return Math.floor(diff / 86_400_000);
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function deriveStatusLabel(r: QueueRecord): { label: string; className: string } {
  if (r.delivery_status === 'sent_outside_sidecar') {
    return { label: 'Sent outside Sidecar', className: 'text-slate-500' };
  }
  if (r.delivery_status === 'sent' || r.status === 'sent') {
    return { label: 'Sent', className: 'text-green-600' };
  }
  if (r.status === 'error') {
    return { label: 'Parse failed', className: 'text-red-600 font-medium' };
  }
  if (r.status === 'pending') {
    return { label: 'Awaiting parse', className: 'text-slate-500' };
  }
  if (r.status === 'pending_manual_review') {
    return { label: 'Manual review', className: 'text-amber-600' };
  }
  if (r.status === 'approved') {
    return { label: 'Approved, not sent', className: 'text-blue-600' };
  }
  if (r.status === 'parsed') {
    if (r.pdf_s3_key || r.template_s3_key) {
      return { label: 'Document generated', className: 'text-indigo-600' };
    }
    const cc = r.fh_claim_data?.claim_count;
    if (cc != null && cc > 0) {
      return { label: `Parsed, ${cc} claim${cc === 1 ? '' : 's'}`, className: 'text-slate-700' };
    }
    return { label: 'Parsed, no claims', className: 'text-slate-600' };
  }
  return { label: r.status || '—', className: 'text-slate-500' };
}

function resolveRequester(r: QueueRecord): string {
  if (r.policy_data?.broker_name) return r.policy_data.broker_name;
  if (r.email_from) return r.email_from;
  return '—';
}

function docsLabel(r: QueueRecord): string {
  const hasPdf = !!r.pdf_s3_key;
  const hasDocx = !!r.template_s3_key;
  if (hasPdf && hasDocx) return 'PDF + DOCX';
  if (hasPdf) return 'PDF';
  if (hasDocx) return 'DOCX';
  return '—';
}

function lossPeriod(r: QueueRecord): string {
  const p = r.extraction_data?.period_requested;
  if (!p || p === 'unknown') return '—';
  return p;
}

function claimCountStr(r: QueueRecord): string {
  if (!r.fh_claim_data) return '—';
  const cc = r.fh_claim_data.claim_count;
  if (cc == null) return '—';
  return String(cc);
}

type SortKey = 'age' | 'received_at' | 'reference' | 'insured' | 'triage_class' | 'status' | 'requester' | 'loss_period' | 'claims' | 'docs';
type SortDir = 'asc' | 'desc';

export default function QueueTriage() {
  const [records, setRecords] = useState<QueueRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [ageFilter, setAgeFilter] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('age');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const [modalAction, setModalAction] = useState<TriageAction | null>(null);
  const [typedConfirm, setTypedConfirm] = useState('');
  const [archiveReason, setArchiveReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resultModal, setResultModal] = useState<TriageResult | null>(null);

  const [drawerId, setDrawerId] = useState<string | null>(null);
  const [drawerData, setDrawerData] = useState<any>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerError, setDrawerError] = useState('');

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.getConvexRequests();
      setRecords(data.requests || []);
    } catch (e: any) {
      setError(e.message || 'Failed to load queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const filtered = useMemo(() => {
    let items = [...records];
    if (ageFilter) {
      items = items.filter(r => ageDays(r.received_at) > 7);
    }
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase();
      items = items.filter(r =>
        (r.extracted_reference_number || '').toLowerCase().includes(q) ||
        (r.extracted_insured_name || '').toLowerCase().includes(q) ||
        resolveRequester(r).toLowerCase().includes(q)
      );
    }
    items.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'age':
          cmp = ageDays(a.received_at) - ageDays(b.received_at);
          break;
        case 'received_at':
          cmp = (a.received_at || '').localeCompare(b.received_at || '');
          break;
        case 'reference':
          cmp = (a.extracted_reference_number || '').localeCompare(b.extracted_reference_number || '');
          break;
        case 'insured':
          cmp = (a.extracted_insured_name || '').localeCompare(b.extracted_insured_name || '');
          break;
        case 'triage_class':
          cmp = (a.triage_class || '').localeCompare(b.triage_class || '');
          break;
        case 'status':
          cmp = deriveStatusLabel(a).label.localeCompare(deriveStatusLabel(b).label);
          break;
        case 'requester':
          cmp = resolveRequester(a).localeCompare(resolveRequester(b));
          break;
        case 'loss_period':
          cmp = lossPeriod(a).localeCompare(lossPeriod(b));
          break;
        case 'claims':
          cmp = (a.fh_claim_data?.claim_count ?? -1) - (b.fh_claim_data?.claim_count ?? -1);
          break;
        case 'docs':
          cmp = docsLabel(a).localeCompare(docsLabel(b));
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return items;
  }, [records, ageFilter, searchText, sortKey, sortDir]);

  const selectedRecords = useMemo(
    () => filtered.filter(r => selected.has(r.request_id)),
    [filtered, selected]
  );

  const allFilteredSelected = filtered.length > 0 && filtered.every(r => selected.has(r.request_id));

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'age' ? 'desc' : 'asc');
    }
  }

  function toggleSelect(id: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (allFilteredSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(r => r.request_id)));
    }
  }

  const openDrawer = useCallback(async (id: string) => {
    setDrawerId(id);
    setDrawerLoading(true);
    setDrawerError('');
    setDrawerData(null);
    try {
      const data = await api.getConvexRequest(id);
      setDrawerData(data);
    } catch (e: any) {
      setDrawerError(e.message || 'Failed to load record details');
    } finally {
      setDrawerLoading(false);
    }
  }, []);

  function closeDrawer() {
    setDrawerId(null);
    setDrawerData(null);
    setDrawerError('');
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && drawerId && !modalAction) {
        closeDrawer();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerId, modalAction]);

  function openActionModal(action: TriageAction) {
    setModalAction(action);
    setTypedConfirm('');
    setArchiveReason('');
  }

  async function executeAction() {
    if (!modalAction) return;
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const result: TriageResult = await api.batchTriage({
        action: modalAction,
        request_ids: Array.from(selected),
        ...(modalAction !== 'delete' && archiveReason.trim() ? { reason: archiveReason.trim() } : {}),
      });
      setResultModal(result);
      setModalAction(null);

      if (result.succeeded.length > 0) {
        const disposed = new Set(result.succeeded);
        setRecords(prev => prev.filter(r => !disposed.has(r.request_id)));
        setSelected(prev => {
          const next = new Set(prev);
          disposed.forEach(id => next.delete(id));
          return next;
        });
        setSuccess(`${ACTION_META[modalAction].label}: ${result.succeeded.length} record${result.succeeded.length === 1 ? '' : 's'} processed.`);
      }
    } catch (e: any) {
      setError(e.message || 'Action failed');
      setModalAction(null);
    } finally {
      setSubmitting(false);
    }
  }

  function sortIndicator(key: SortKey) {
    if (sortKey !== key) return '';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  }

  const needsTypedConfirm = selected.size > 10;
  const confirmWord = modalAction ? ACTION_META[modalAction].confirmWord : '';
  const typedCorrectly = typedConfirm.toUpperCase() === confirmWord;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Queue Triage</h1>
        <p className="text-sm text-slate-500 mt-1">
          Clear the aged Convex loss-run backlog. Archive, mark as manually sent, or soft-delete records in bulk.
        </p>
      </div>

      {/* Banners */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-400 hover:text-red-600 ml-2">✕</button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center justify-between">
          <span>{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-400 hover:text-green-600 ml-2">✕</button>
        </div>
      )}

      {/* Controls bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={ageFilter}
            onChange={e => setAgeFilter(e.target.checked)}
            className="rounded border-slate-300"
          />
          Age &gt; 7 days only
        </label>
        <input
          type="text"
          placeholder="Search ref # or insured name..."
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          className="px-3 py-1.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 w-64"
        />
        <div className="flex-1" />
        {selected.size > 0 && (
          <span className="text-sm font-medium text-slate-700">
            {selected.size} selected
          </span>
        )}
        <button
          onClick={() => openActionModal('archive')}
          disabled={selected.size === 0}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${ACTION_META.archive.btnClass}`}
        >
          Mark Archived
        </button>
        <button
          onClick={() => openActionModal('manual_send_archive')}
          disabled={selected.size === 0}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${ACTION_META.manual_send_archive.btnClass}`}
        >
          Manually Sent + Archived
        </button>
        <button
          onClick={() => openActionModal('delete')}
          disabled={selected.size === 0}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${ACTION_META.delete.btnClass}`}
        >
          Delete
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-12 text-center">
          <svg className="animate-spin h-8 w-8 text-blue-500 mx-auto mb-3" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          <p className="text-sm text-slate-500">Loading queue...</p>
        </div>
      )}

      {/* Empty state */}
      {!loading && filtered.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-12 text-center text-slate-500">
          {records.length === 0
            ? 'No records in the queue.'
            : 'No records match the current filters.'}
        </div>
      )}

      {/* Table */}
      {!loading && filtered.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={toggleSelectAll}
                      className="rounded border-slate-300"
                    />
                  </th>
                  <ThSort label="Age" sortKey="age" current={sortKey} dir={sortDir} onSort={toggleSort} indicator={sortIndicator('age')} />
                  <ThSort label="Received" sortKey="received_at" current={sortKey} dir={sortDir} onSort={toggleSort} indicator={sortIndicator('received_at')} />
                  <ThSort label="Reference" sortKey="reference" current={sortKey} dir={sortDir} onSort={toggleSort} indicator={sortIndicator('reference')} />
                  <ThSort label="Insured" sortKey="insured" current={sortKey} dir={sortDir} onSort={toggleSort} indicator={sortIndicator('insured')} />
                  <ThSort label="Triage" sortKey="triage_class" current={sortKey} dir={sortDir} onSort={toggleSort} indicator={sortIndicator('triage_class')} />
                  <ThSort label="Status" sortKey="status" current={sortKey} dir={sortDir} onSort={toggleSort} indicator={sortIndicator('status')} />
                  <ThSort label="Loss Period" sortKey="loss_period" current={sortKey} dir={sortDir} onSort={toggleSort} indicator={sortIndicator('loss_period')} />
                  <ThSort label="Claims" sortKey="claims" current={sortKey} dir={sortDir} onSort={toggleSort} indicator={sortIndicator('claims')} />
                  <ThSort label="Docs" sortKey="docs" current={sortKey} dir={sortDir} onSort={toggleSort} indicator={sortIndicator('docs')} />
                  <ThSort label="Requester / Broker" sortKey="requester" current={sortKey} dir={sortDir} onSort={toggleSort} indicator={sortIndicator('requester')} />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(r => {
                  const age = ageDays(r.received_at);
                  const statusInfo = deriveStatusLabel(r);
                  const requester = resolveRequester(r);
                  const docs = docsLabel(r);
                  return (
                    <tr key={r.request_id} className={`hover:bg-slate-50 ${selected.has(r.request_id) ? 'bg-blue-50' : ''}`}>
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selected.has(r.request_id)}
                          onChange={() => toggleSelect(r.request_id)}
                          className="rounded border-slate-300"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`font-medium ${age > 30 ? 'text-red-600' : age > 14 ? 'text-amber-600' : 'text-slate-700'}`}>
                          {age}d
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">{formatDate(r.received_at)}</td>
                      <td className="px-4 py-3 text-sm font-mono text-slate-800">
                        <span className="cursor-pointer hover:text-blue-600 hover:underline" onClick={() => openDrawer(r.request_id)}>
                          {r.extracted_reference_number || '—'}
                        </span>
                        {r.dup_of && (
                          <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-orange-100 text-orange-700 uppercase">dup</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-800 max-w-xs truncate">{r.extracted_insured_name || '—'}</td>
                      <td className="px-4 py-3">
                        <TriageBadge value={r.triage_class} />
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        <span className={statusInfo.className}>{statusInfo.label}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">{lossPeriod(r)}</td>
                      <td className="px-4 py-3 text-sm text-slate-700 text-center">{claimCountStr(r)}</td>
                      <td className="px-4 py-3 text-sm">
                        {docs !== '—' ? (
                          <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">{docs}</span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 max-w-[12rem] truncate" title={requester}>
                        {requester}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 bg-slate-50 border-t border-slate-200 text-xs text-slate-500">
            {filtered.length} record{filtered.length === 1 ? '' : 's'}
            {ageFilter ? ' (filtered: age > 7 days)' : ''}
            {records.length !== filtered.length ? ` of ${records.length} total` : ''}
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {modalAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => !submitting && setModalAction(null)}>
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-lg w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-800 mb-1">{ACTION_META[modalAction].label}</h3>
            <p className="text-sm text-slate-600 mb-4">{ACTION_META[modalAction].description}</p>

            {!needsTypedConfirm ? (
              /* <= 10 records: show full preview list */
              <div className="max-h-64 overflow-y-auto mb-4 border border-slate-200 rounded-lg divide-y divide-slate-100">
                {selectedRecords.map(r => (
                  <div key={r.request_id} className="px-3 py-2 text-sm">
                    <span className="font-mono text-slate-800">{r.extracted_reference_number || '—'}</span>
                    <span className="mx-2 text-slate-300">|</span>
                    <span className="text-slate-600">{r.extracted_insured_name || '—'}</span>
                    <span className="mx-2 text-slate-300">|</span>
                    <span className={`${ageDays(r.received_at) > 30 ? 'text-red-600' : 'text-slate-500'}`}>
                      {ageDays(r.received_at)}d old
                    </span>
                    <span className="mx-2 text-slate-300">|</span>
                    <span className="text-slate-500">{r.delivery_status || r.status || '—'}</span>
                  </div>
                ))}
              </div>
            ) : (
              /* > 10 records: show sample + typed confirmation */
              <div className="mb-4">
                <p className="text-sm text-slate-600 mb-2">
                  <span className="font-semibold">{selected.size}</span> records selected. Sample:
                </p>
                <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100 mb-3">
                  {selectedRecords.slice(0, 5).map(r => (
                    <div key={r.request_id} className="px-3 py-1.5 text-xs">
                      <span className="font-mono text-slate-700">{r.extracted_reference_number || '—'}</span>
                      <span className="mx-1.5 text-slate-300">|</span>
                      <span className="text-slate-600">{r.extracted_insured_name || '—'}</span>
                    </div>
                  ))}
                  {selectedRecords.length > 10 && (
                    <div className="px-3 py-1.5 text-xs text-slate-400 text-center">
                      ... {selected.size - 10} more ...
                    </div>
                  )}
                  {selectedRecords.slice(-5).map(r => (
                    <div key={r.request_id} className="px-3 py-1.5 text-xs">
                      <span className="font-mono text-slate-700">{r.extracted_reference_number || '—'}</span>
                      <span className="mx-1.5 text-slate-300">|</span>
                      <span className="text-slate-600">{r.extracted_insured_name || '—'}</span>
                    </div>
                  ))}
                </div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Type <span className="font-mono font-bold text-slate-900">{confirmWord}</span> to confirm:
                </label>
                <input
                  type="text"
                  value={typedConfirm}
                  onChange={e => setTypedConfirm(e.target.value)}
                  className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm font-mono focus:ring-2 focus:ring-blue-500"
                  placeholder={confirmWord}
                  autoFocus
                />
              </div>
            )}

            {modalAction !== 'delete' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Archive reason <span className="text-slate-400 font-normal">(optional, recorded on each record)</span>
                </label>
                <textarea
                  value={archiveReason}
                  onChange={e => setArchiveReason(e.target.value)}
                  maxLength={500}
                  rows={2}
                  className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. aged backlog, requester confirmed no longer needed"
                />
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setModalAction(null)}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={executeAction}
                disabled={submitting || (needsTypedConfirm && !typedCorrectly)}
                className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${ACTION_META[modalAction].btnClass}`}
              >
                {submitting ? 'Processing...' : `Confirm ${ACTION_META[modalAction].label}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Result Modal (partial success) */}
      {resultModal && (resultModal.skipped.length > 0 || resultModal.failed.length > 0) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setResultModal(null)}>
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-lg w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-800 mb-3">Action Results</h3>
            {resultModal.succeeded.length > 0 && (
              <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                {resultModal.succeeded.length} succeeded
              </div>
            )}
            {resultModal.skipped.length > 0 && (
              <div className="mb-3">
                <p className="text-sm font-medium text-amber-700 mb-1">Skipped ({resultModal.skipped.length}):</p>
                <div className="max-h-32 overflow-y-auto border border-amber-200 rounded-lg divide-y divide-amber-100 bg-amber-50">
                  {resultModal.skipped.map(s => (
                    <div key={s.request_id} className="px-3 py-1.5 text-xs text-amber-800">
                      <span className="font-mono">{s.request_id.slice(0, 8)}...</span> — {s.reason}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {resultModal.failed.length > 0 && (
              <div className="mb-3">
                <p className="text-sm font-medium text-red-700 mb-1">Failed ({resultModal.failed.length}):</p>
                <div className="max-h-32 overflow-y-auto border border-red-200 rounded-lg divide-y divide-red-100 bg-red-50">
                  {resultModal.failed.map(f => (
                    <div key={f.request_id} className="px-3 py-1.5 text-xs text-red-800">
                      <span className="font-mono">{f.request_id.slice(0, 8)}...</span> — {f.reason}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex justify-end">
              <button
                onClick={() => setResultModal(null)}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Drawer */}
      {drawerId && (
        <DetailDrawer
          data={drawerData}
          loading={drawerLoading}
          error={drawerError}
          onClose={closeDrawer}
        />
      )}
    </div>
  );
}

function ThSort({ label, sortKey, current, dir, onSort, indicator }: {
  label: string; sortKey: SortKey; current: SortKey; dir: SortDir;
  onSort: (k: SortKey) => void; indicator: string;
}) {
  return (
    <th
      onClick={() => onSort(sortKey)}
      className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none"
    >
      {label}{indicator}
    </th>
  );
}

function TriageBadge({ value }: { value?: string }) {
  if (!value) return <span className="text-sm text-slate-400">—</span>;
  const colors: Record<string, string> = {
    A: 'bg-green-100 text-green-700',
    B: 'bg-amber-100 text-amber-700',
    C: 'bg-blue-100 text-blue-700',
    D: 'bg-slate-100 text-slate-600',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[value] || 'bg-slate-100 text-slate-600'}`}>
      {value}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Detail Drawer (Phase 3)
   ═══════════════════════════════════════════════════════════════ */

function DrawerSection({ title, defaultOpen = true, children }: {
  title: string; defaultOpen?: boolean; children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 hover:bg-slate-50"
      >
        {title}
        <span className="text-slate-400">{open ? '▾' : '▸'}</span>
      </button>
      {open && <div className="px-5 pb-4 space-y-2">{children}</div>}
    </div>
  );
}

function DrawerField({ label, value, mono }: { label: string; value?: string | null; mono?: boolean }) {
  const display = value || '—';
  return (
    <div className="flex text-sm">
      <span className="w-32 flex-shrink-0 text-slate-500">{label}</span>
      <span className={`text-slate-800 break-words min-w-0 ${mono ? 'font-mono text-xs' : ''}`}>{display}</span>
    </div>
  );
}

function DetailDrawer({ data, loading, error, onClose }: {
  data: any; loading: boolean; error: string; onClose: () => void;
}) {
  if (!data && !loading && !error) return null;

  const age = data?.received_at ? ageDays(data.received_at) : null;
  const statusInfo = data ? deriveStatusLabel(data) : null;

  const recipients = data?.original_recipients_backup;
  const toList: string[] = (recipients?.to || []).map((r: any) => r.email).filter(Boolean);
  const ccList: string[] = (recipients?.cc || []).map((r: any) => r.email).filter(Boolean);

  const attempts: any[] = data?.send_attempts || [];

  const claims: any[] = data?.fh_claim_data?.claims || [];
  const claimNums = claims.map((c: any) => c.claim_number || c.claimant_name).filter(Boolean);

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} />

      {/* Drawer panel */}
      <div className="fixed inset-y-0 right-0 z-50 w-full sm:w-[36rem] bg-white shadow-2xl border-l border-slate-200 flex flex-col overflow-hidden">
        {/* Close bar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50 flex-shrink-0">
          <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Record Detail</span>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-lg leading-none">&times;</button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="p-12 text-center">
              <svg className="animate-spin h-6 w-6 text-blue-500 mx-auto mb-2" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              <p className="text-sm text-slate-500">Loading...</p>
            </div>
          )}

          {error && (
            <div className="m-5 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
          )}

          {data && !loading && (
            <>
              {/* Header */}
              <div className="px-5 py-4 border-b border-slate-200">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-slate-800 font-mono">
                      {data.extracted_reference_number || '—'}
                    </h2>
                    <p className="text-sm text-slate-600 mt-0.5">{data.extracted_insured_name || '—'}</p>
                  </div>
                  {data.triage_class && <TriageBadge value={data.triage_class} />}
                </div>
                <div className="flex items-center gap-4 mt-2 text-sm">
                  {statusInfo && <span className={statusInfo.className}>{statusInfo.label}</span>}
                  {age != null && (
                    <span className={`font-medium ${age > 30 ? 'text-red-600' : age > 14 ? 'text-amber-600' : 'text-slate-600'}`}>
                      {age}d old
                    </span>
                  )}
                  <span className="text-slate-400">{formatDate(data.received_at)}</span>
                </div>
              </div>

              {/* Request Context */}
              <DrawerSection title="Request Context">
                <DrawerField label="Email subject" value={data.email_subject} />
                <DrawerField label="From" value={data.email_from} mono />
                {toList.length > 0 && <DrawerField label="To" value={toList.join(', ')} mono />}
                {ccList.length > 0 && <DrawerField label="CC" value={ccList.join(', ')} mono />}
                <DrawerField label="Loss period" value={data.extraction_data?.period_requested !== 'unknown' ? data.extraction_data?.period_requested : undefined} />
                <DrawerField label="Broker" value={data.policy_data?.broker_name} />
                <DrawerField label="Queue type" value={data.queue_type} />
                <DrawerField label="Insurer" value={data.insurer_name || data.insurer} />
              </DrawerSection>

              {/* Extraction State */}
              <DrawerSection title="Extraction State">
                <DrawerField label="Triage class" value={data.triage_class} />
                <DrawerField label="Triage reason" value={data.triage_reason} />
                <DrawerField label="Confidence" value={data.extraction_confidence || data.extraction_data?.confidence} />
                <DrawerField label="Parsed at" value={formatDate(data.parsed_at)} />
                {data.extraction_data?.notes && (
                  <div className="text-sm">
                    <p className="text-slate-500 mb-1">Parser notes</p>
                    <p className="text-slate-700 text-xs bg-slate-50 rounded p-2 leading-relaxed">{data.extraction_data.notes}</p>
                  </div>
                )}
                <DrawerField label="Claim count" value={data.fh_claim_data?.claim_count != null ? String(data.fh_claim_data.claim_count) : undefined} />
                {claimNums.length > 0 && (
                  <div className="text-sm">
                    <p className="text-slate-500 mb-1">Claims ({claimNums.length > 5 ? `first 5 of ${claimNums.length}` : claimNums.length})</p>
                    <div className="flex flex-wrap gap-1">
                      {claimNums.slice(0, 5).map((cn: string, i: number) => (
                        <span key={i} className="px-1.5 py-0.5 bg-slate-100 rounded text-xs font-mono text-slate-700">{cn}</span>
                      ))}
                      {claimNums.length > 5 && <span className="text-xs text-slate-400">+{claimNums.length - 5} more</span>}
                    </div>
                  </div>
                )}
              </DrawerSection>

              {/* Documents */}
              <DrawerSection title="Documents">
                {data.template_download_url ? (
                  <a
                    href={data.template_download_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {data.download_is_pdf ? 'Download PDF' : 'Download DOCX'}
                  </a>
                ) : (
                  <>
                    <DrawerField label="PDF" value={data.pdf_s3_key ? 'Generated' : undefined} />
                    <DrawerField label="DOCX" value={data.template_s3_key ? 'Generated' : undefined} />
                  </>
                )}
                {!data.pdf_s3_key && !data.template_s3_key && (
                  <p className="text-sm text-slate-400">No documents generated</p>
                )}
              </DrawerSection>

              {/* Send History */}
              <DrawerSection title="Send History" defaultOpen={attempts.length > 0}>
                {attempts.length === 0 ? (
                  <p className="text-sm text-slate-400">Never attempted</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-slate-500 border-b border-slate-100">
                          <th className="pb-1 pr-3">Timestamp</th>
                          <th className="pb-1 pr-3">Status</th>
                          <th className="pb-1 pr-3">Channel</th>
                          <th className="pb-1">Message ID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {attempts.map((a: any, i: number) => (
                          <tr key={i} className="border-b border-slate-50">
                            <td className="py-1.5 pr-3 text-slate-600 whitespace-nowrap">{formatDate(a.timestamp)}</td>
                            <td className="py-1.5 pr-3">
                              <span className={a.status === 'sent' ? 'text-green-600' : 'text-slate-600'}>{a.status || '—'}</span>
                            </td>
                            <td className="py-1.5 pr-3 text-slate-600">{a.channel || '—'}</td>
                            <td className="py-1.5 font-mono text-slate-500 truncate max-w-[10rem]" title={a.message_id}>{a.message_id || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </DrawerSection>

              {/* Duplicates */}
              {data.dup_of && (
                <DrawerSection title="Duplicates">
                  <DrawerField label="Duplicate of" value={data.dup_of} mono />
                </DrawerSection>
              )}

              {/* Audit Trail */}
              <DrawerSection title="Audit Trail" defaultOpen={false}>
                <DrawerField label="Request ID" value={data.request_id} mono />
                <DrawerField label="Received" value={formatDate(data.received_at)} />
                <DrawerField label="Parsed" value={formatDate(data.parsed_at)} />
                {data.approved_at && <DrawerField label="Approved at" value={formatDate(data.approved_at)} />}
                {data.approved_by && <DrawerField label="Approved by" value={data.approved_by} />}
                {data.archived_at && <DrawerField label="Archived at" value={formatDate(data.archived_at)} />}
                {data.archived_by && <DrawerField label="Archived by" value={data.archived_by} />}
                {data.archived_reason && <DrawerField label="Archive reason" value={data.archived_reason} />}
                {data.archived_note && <DrawerField label="Archive note" value={data.archived_note} />}
                {data.deleted_at && <DrawerField label="Deleted at" value={formatDate(data.deleted_at)} />}
                {data.deleted_by && <DrawerField label="Deleted by" value={data.deleted_by} />}
                {data.email_message_id && <DrawerField label="Email message ID" value={data.email_message_id} mono />}
              </DrawerSection>

              {/* Email body preview */}
              {data.email_body && (
                <DrawerSection title="Email Body" defaultOpen={false}>
                  <pre className="text-xs text-slate-700 bg-slate-50 rounded p-3 whitespace-pre-wrap max-h-64 overflow-y-auto leading-relaxed">{data.email_body}</pre>
                </DrawerSection>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
